import { useEffect } from 'react'

import {
  useGetUserInfoQuery,
  CustomerUserInfoDataFragment,
  CustomerInfoFragment,
  UserType,
  CustomerUserPermissions
} from 'graph/generated/payments/graphql-types'
import Cookies from 'js-cookie'
import { usePostHog } from 'posthog-js/react'
import { AppCookie, cookieOptions } from 'utils/cookies'
import { actionTracking } from 'utils/posthog'

import { useCompanyAndInvoiceContext } from '../providers/CompanyAndInvoiceProvider'

import { selectActiveUser } from './selectActiveUser'

const guestUser: CustomerUserInfoDataFragment = {
  __typename: 'CustomerUserInfo',
  id: '00000000-0000-0000-0000-000000000000',
  role: 'guest',
  email: '',
  createdAt: '',
  permissions: [
    CustomerUserPermissions.BankingNew,
    CustomerUserPermissions.CardManage
  ]
}

/**
 * Hook to extract active user account information. The active membership is
 * picked by `selectActiveUser`; the CustomerId cookie is kept in sync with it
 * because every GraphQL request sends that cookie as `X-Customer-Id`.
 */
const useActiveUserInfo = (): {
  loading: boolean
  user: CustomerUserInfoDataFragment
  customer: CustomerInfoFragment
  userType: UserType
  /**
   * Signed in (valid session), but the account has no active membership in
   * this partner's portal — e.g. it was only linked to a customer the partner
   * archived. Distinct from logged out: signing in again cannot help.
   */
  hasNoPortalMembership: boolean
} => {
  const posthog = usePostHog()
  const customerId = Cookies.get(AppCookie.CustomerId)
  const isLoggedAsGuest = Cookies.get(AppCookie.LoggedAsGuest)

  const { data, loading, error } = useGetUserInfoQuery()
  const { invoice, routeInvoiceId } = useCompanyAndInvoiceContext()
  const user = selectActiveUser(data?.GetUserInfo?.users, {
    cookieCustomerId: customerId,
    invoiceCustomerId: invoice?.customer?.id,
    // The fetched invoice outlives client-side navigation: it only wins
    // while it is the one the current route names.
    preferInvoice: !!invoice?.id && invoice.id === routeInvoiceId
  })
  const activeCustomerId = user?.customer?.id

  // Written during render, not in an effect: children mount in the same
  // commit and fire their queries from their own effects, which run before
  // ours — they must already send the corrected X-Customer-Id. Idempotent.
  if (activeCustomerId && activeCustomerId !== customerId) {
    Cookies.set(
      AppCookie.CustomerId,
      activeCustomerId,
      cookieOptions(AppCookie.CustomerId, { httpOnly: false })
    )
  }

  // The client uses errorPolicy 'all': an invalid session sets `error` and
  // leaves GetUserInfo null — that is "logged out", not "no membership".
  const hasNoPortalMembership =
    !loading && !error && !!data?.GetUserInfo && !data.GetUserInfo.users?.length

  useEffect(() => {
    if (user?.email) {
      Cookies.remove(AppCookie.LoggedAsGuest)
    }

    const partnerId = user?.customer?.partner?.id || invoice?.partner?.id

    if (!posthog || !partnerId) return

    // Check if user is identified and has partnerId property
    const userProperties = posthog.get_property('partnerId')
    const needsReidentification = posthog._isIdentified() && !userProperties

    if (
      posthog._isIdentified() &&
      posthog.sessionRecordingStarted() &&
      !needsReidentification
    )
      return

    if (user?.email) {
      posthog.identify(user.email, {
        userId: user.id,
        email: user.email,
        name: `${user.firstName} ${user.lastName}`,
        customerId: user.customer?.id,
        partnerId: user.customer?.partner?.id,
        role: user.role,
        type: 'customer-user'
      })
    } else if (isLoggedAsGuest === 'true') {
      actionTracking({
        action: 'identify guest',
        properties: {
          customerId: invoice?.customer?.id || 'N/A',
          partnerId: invoice?.partner?.id || 'N/A',
          partnerIDResolved: partnerId || 'N/A'
        }
      })

      // A literal 'guest' id merged every guest payer on every portal into
      // one PostHog person. Scope it to the customer so sessions stay
      // attributable (FIRE-3553); without a customer, stay anonymous.
      const guestCustomerId = invoice?.customer?.id
      if (guestCustomerId) {
        posthog.identify(`guest:${guestCustomerId}`, {
          name: 'Guest',
          role: 'guest',
          customerId: guestCustomerId,
          partnerId: invoice?.partner?.id
        })
      }
    }
    if (posthog.sessionManager) {
      posthog.startSessionRecording()
    }
  }, [user, isLoggedAsGuest, invoice, posthog])

  if (!user?.email && isLoggedAsGuest === 'true') {
    return {
      user: guestUser,
      customer: null,
      loading: false,
      userType: null,
      hasNoPortalMembership: false
    }
  }
  return {
    user,
    customer: user?.customer,
    loading,
    userType: data?.GetUserInfo?.userType,
    hasNoPortalMembership
  }
}

export default useActiveUserInfo
